import { useEffect, useMemo, useRef, useState } from "react"
import PlotlyChart from "components/plotly-chart"
import { useRegisterDownloadData } from "hooks/use-register-download-data"
import { usePanelTitle } from "connected-components/dashboard/context/panel-id"
import { artifactToDownloadData } from "connected-components/ai-teammate/helpers/artifact-to-download-data"
import type { ArtifactTemplateProps } from "connected-components/dashboard/modules/ai-artifact/types"

import "./plot.scss"

export default function PlotTemplate({ artifact, currency }: ArtifactTemplateProps) {
  const panelTitle = usePanelTitle()
  const downloadRegistration = useMemo(() => ({
    key: `ai-artifact-plot-${artifact.id}`,
    sheetName: panelTitle ?? "AI Chart",
    getData: () => artifactToDownloadData(artifact, currency),
  }), [artifact, currency, panelTitle])
  useRegisterDownloadData(downloadRegistration)
  const containerRef = useRef<HTMLDivElement>(null)
  const [renderKey, setRenderKey] = useState(0)
  const lastVisibilityState = useRef<{ width: number, height: number } | null>(null)

  // Monitor visibility and size changes to trigger re-renders when needed
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const checkAndUpdateVisibility = () => {
      const rect = container.getBoundingClientRect()

      // Skip re-render logic on initial check — PlotlyChart handles initial render.
      // Only trigger when transitioning from hidden to visible (established by prior state).
      if (lastVisibilityState.current === null) {
        lastVisibilityState.current = { width: rect.width, height: rect.height }
        return
      }

      const wasHidden = lastVisibilityState.current.width === 0 || lastVisibilityState.current.height === 0
      const isNowVisible = rect.width > 0 && rect.height > 0

      if (wasHidden && isNowVisible) {
        setRenderKey((prev) => prev + 1)
      }

      lastVisibilityState.current = { width: rect.width, height: rect.height }
    }

    // Establish initial state (no re-render — avoids double Plotly.newPlot on mount)
    checkAndUpdateVisibility()

    // Observe visibility changes with IntersectionObserver
    const setupIntersectionObserver = () => {
      if (typeof IntersectionObserver === "undefined") return

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              checkAndUpdateVisibility()
            }
          })
        },
        { threshold: 0.01 },
      )

      observer.observe(container)
      return () => observer.disconnect()
    }

    // Observe size changes with ResizeObserver
    const setupResizeObserver = () => {
      if (typeof ResizeObserver === "undefined") return

      const resizeObserver = new ResizeObserver(() => {
        checkAndUpdateVisibility()
      })

      resizeObserver.observe(container)
      return () => resizeObserver.disconnect()
    }

    const cleanupIntersection = setupIntersectionObserver()
    const cleanupResize = setupResizeObserver()

    return () => {
      cleanupIntersection?.()
      cleanupResize?.()
    }
  }, [])

  if (artifact.type !== "plot") {
    return null
  }

  const { data, layout, config, ...rest } = artifact.figure

  return (
    <div ref={containerRef} className="dashboard-plot-artifact">
      <PlotlyChart
        key={`plot-${artifact.id}-${renderKey}`}
        className="dashboard-plot-artifact__chart"
        data={data}
        layout={layout}
        config={config}
        {...rest}
      />
    </div>
  )
}
